(() => {
  if (customElements.get('shopde-discovery')) return;
  const STORAGE_KEY = 'sd247-history-v1';
  const MAX_HISTORY = 24;
  const MAX_AGE = 30 * 24 * 60 * 60 * 1000;
  class ShopdeDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.config = JSON.parse(this.querySelector('[data-discovery-config]').textContent);
      this.words = this.config.words || {};
      this.cache = new Map();
      this.queue = [];
      this.active = 0;
      this.generation = 0;
      this.allowed = false;
      this.state = { enabled: true, items: [] };
      this.historyCards = [];
      this.rows = Object.fromEntries([...this.querySelectorAll('[data-discovery-row]')].map(row => [row.dataset.discoveryRow, row]));
      this.sources = [...this.querySelector('[data-discovery-sources]').content.querySelectorAll('a')].map(a => ({ world: a.dataset.world, category: a.dataset.category, url: this.collectionURL(a.getAttribute('href')) })).filter(s => s.url);
      if (this.config.type === 'world' && !this.sources.some(s => s.world === this.config.world)) {
        this.config.world = document.querySelector('[data-shopde-active-world]')?.dataset.shopdeActiveWorld || '';
        if (!this.config.world) this.config.type = 'index';
      }
      this.dialog = this.querySelector('dialog');
      this.listen(this, 'click', event => this.onClick(event));
      this.listen(this.querySelector('[data-history-enabled]'), 'change', event => this.setEnabled(event.target.checked));
      this.listen(this.dialog, 'close', () => this.opener?.focus({ preventScroll: true }));
      this.listen(document, 'visitorConsentCollected', () => this.checkPrivacy());
      this.listen(window, 'storage', event => {
        if (event.key === STORAGE_KEY && this.allowed) { this.readHistory(); this.refresh(); }
      });
      this.listen(window, 'resize', () => Object.values(this.rows).forEach(row => this.updateCarousel(row)));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCart());
      }
      for (const row of Object.values(this.rows)) {
        this.listen(row.querySelector('[data-row-rail]'), 'scroll', () => this.updateCarousel(row), { passive: true });
        this.listen(row.querySelector('[data-row-rail]'), 'keydown', event => this.onKey(event, row));
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { this.ready = true; this.observer.disconnect(); this.refresh(); }
        }, { rootMargin: '500px' });
        this.observer.observe(this);
      } else { this.ready = true; }
      this.loadPrivacy();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      clearTimeout(this.privacyTimer);
      this.unsubscribeCart?.();
      this.queue.splice(0).forEach(job => job.resolve([]));
      this.controller = null;
    }
    listen(target, event, callback, options = {}) { target?.addEventListener(event, callback, { ...options, signal: this.signal }); }
    word(key, fallback) { return this.words[key] || fallback; }
    url(path) {
      try {
        const url = new URL(path, location.origin);
        if (url.origin !== location.origin) return null;
        const theme = window.Shopify?.theme;
        const preview = new URLSearchParams(location.search).get('preview_theme_id') || (['development', 'unpublished'].includes(theme?.role) ? theme.id : null);
        if (preview) url.searchParams.set('preview_theme_id', preview);
        return url;
      } catch { return null; }
    }
    collectionURL(path) {
      if (!path?.trim()) return null;
      const url = this.url(path.trim());
      if (!url || !/\/collections\/[^/]+(?:\/[^/]+)?\/?$/.test(url.pathname)) return null;
      if (/\/collections\/(all|all-products)\/?$/.test(url.pathname)) return null;
      url.searchParams.delete('view');
      url.searchParams.delete('page');
      return url.href;
    }
    context(value) {
      if (!value || !/^\d+$/.test(String(value.id)) || !/^[a-z0-9][a-z0-9-]{0,254}$/.test(value.handle || '')) return null;
      return { id: String(value.id), handle: value.handle, world: String(value.world || '').slice(0, 70), category: String(value.category || '').slice(0, 180), scope: this.collectionURL(value.scope) || '' };
    }
    async loadPrivacy(attempt = 0) {
      if (this.signal.aborted) return;
      if (window.Shopify?.customerPrivacy) { this.checkPrivacy(); return; }
      if (window.Shopify?.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => { if (!this.signal.aborted) this.checkPrivacy(); });
      }
      if (attempt < 8) this.privacyTimer = setTimeout(() => this.loadPrivacy(attempt + 1), 500);
      else this.checkPrivacy();
    }
    checkPrivacy() {
      if (this.signal.aborted) return;
      const privacy = window.Shopify?.customerPrivacy;
      let allowed = false;
      try { allowed = privacy?.preferencesProcessingAllowed() === true; } catch {}
      const changed = this.allowed !== allowed;
      this.allowed = allowed;
      if (allowed) {
        this.readHistory();
        this.recordCurrent();
      } else {
        this.state = { enabled: true, items: [] };
        this.historyCards = [];
        try { localStorage.removeItem(STORAGE_KEY); } catch {}
      }
      this.updateManager();
      if (changed || this.ready) this.refresh();
    }
    readHistory() {
      if (!this.allowed) return;
      try {
        let raw = null;
        const stored = localStorage.getItem(STORAGE_KEY);
        try { raw = JSON.parse(stored || 'null'); } catch { localStorage.removeItem(STORAGE_KEY); }
        const ids = new Set();
        const now = Date.now();
        this.state = { enabled: raw?.enabled !== false, items: (Array.isArray(raw?.items) ? raw.items : []).flatMap(item => {
          const context = this.context(item);
          if (!context || !Number.isFinite(item.at) || item.at > now + 60000 || now - item.at > MAX_AGE || ids.has(context.id)) return [];
          ids.add(context.id);
          return [{ ...context, at: item.at }];
        }).slice(0, MAX_HISTORY) };
        if (!this.state.enabled) this.state.items = [];
        this.storageFailed = false;
        if (Array.isArray(raw?.items) && raw.items.length !== this.state.items.length) this.persist();
      } catch { this.state = { enabled: true, items: [] }; this.storageFailed = true; }
    }
    persist() {
      if (!this.allowed) return false;
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state)); this.storageFailed = false; return true; }
      catch { this.storageFailed = true; this.state.items = []; this.historyCards = []; return false; }
    }
    recordCurrent() {
      const current = this.context(this.config.current);
      if (!current || this.recorded || !this.allowed || !this.state.enabled || this.storageFailed) return;
      this.recorded = true;
      this.state.items = [{ ...current, at: Date.now() }, ...this.state.items.filter(item => item.id !== current.id)].slice(0, MAX_HISTORY);
      this.persist();
    }
    setEnabled(enabled) {
      if (!this.allowed) return;
      this.state = { enabled, items: [] };
      this.recorded = !enabled;
      this.persist();
      if (enabled) this.recordCurrent();
      this.refresh();
    }
    request(path, parameters = {}) {
      const url = this.url(path);
      if (!url) return Promise.resolve([]);
      url.searchParams.set('section_id', 'shopde-discovery-data');
      Object.entries(parameters).forEach(([key, value]) => url.searchParams.set(key, value));
      const key = url.href;
      if (this.cache.has(key)) return this.cache.get(key);
      const promise = new Promise(resolve => { this.queue.push({ url: key, resolve }); this.pump(); });
      this.cache.set(key, promise);
      return promise;
    }
    pump() {
      while (this.active < 4 && this.queue.length && !this.signal.aborted) {
        const job = this.queue.shift();
        this.active++;
        fetch(job.url, { signal: AbortSignal.any([this.signal, AbortSignal.timeout(20000)]), credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(response => response.ok ? response.text() : '')
          .then(html => {
            if (this.signal.aborted || !html) return [];
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return [...doc.querySelectorAll('[data-discovery-response] [data-discovery-product]')].flatMap(node => {
              try {
                const context = this.context(JSON.parse(node.dataset.context));
                const link = node.querySelector('a');
                if (!context || !link || !this.url(link.getAttribute('href'))) return [];
                return [{ context, node, available: node.dataset.available === 'true' }];
              } catch { return []; }
            });
          }).catch(() => []).then(cards => job.resolve(cards)).finally(() => { this.active--; this.pump(); });
      }
    }
    product(context) {
      const valid = this.context(context);
      if (!valid) return Promise.resolve(null);
      return this.request(`${this.config.root.replace(/\/$/, '')}/products/${valid.handle}`).then(cards => cards.find(card => card.context.id === valid.id) || null);
    }
    async hydrateHistory(limit) {
      const entries = this.allowed && this.state.enabled ? this.state.items.slice(0, limit) : [];
      const cards = await Promise.all(entries.map(entry => this.product(entry)));
      return cards.filter(Boolean);
    }
    seedSources(seeds) {
      const sources = [];
      for (const seed of seeds) {
        if (seed.scope) sources.push({ world: seed.world, category: seed.category, url: seed.scope });
        sources.push(...this.sources.filter(source => source.world === seed.world && source.category === seed.category));
      }
      return sources;
    }
    async refreshCart() {
      if (this.config.type !== 'cart') return;
      const generation = ++this.generation;
      try {
        const response = await fetch(`${this.config.root.replace(/\/$/, '')}/cart.js`, { signal: this.signal, credentials: 'same-origin' });
        if (!response.ok) return;
        const cart = await response.json();
        if (this.signal.aborted || generation !== this.generation) return;
        this.config.excluded = cart.items.map(item => String(item.product_id));
        const products = await Promise.all(cart.items.slice(0, 4).map(item => this.product({ id: String(item.product_id), handle: item.handle })));
        if (this.signal.aborted || generation !== this.generation) return;
        this.config.cart = products.filter(Boolean).map(card => card.context);
        this.refresh();
      } catch {}
    }
    async candidates(history) {
      const current = this.context(this.config.current);
      const cart = this.config.cart.map(item => this.context(item)).filter(Boolean);
      const historySeeds = history.slice(0, 4).map(card => card.context);
      const collectionSource = this.config.type === 'collection' ? this.collectionURL(this.config.collection) : null;
      let seeds = [], sources = [], personalized = false;
      if (collectionSource) {
        sources = [{ world: this.config.world, url: collectionSource, currentCollection: true },
          ...this.sources.filter(source => this.config.world && source.world === this.config.world)];
      } else if (this.config.type === 'world') {
        sources = this.sources.filter(source => source.world === this.config.world);
        seeds = historySeeds.filter(seed => seed.world === this.config.world);
        personalized = seeds.length > 0;
        if (personalized) sources = [...this.seedSources(seeds), ...sources];
      } else if (current) {
        seeds = [current];
        sources = this.seedSources(seeds);
        personalized = historySeeds.some(seed => seed.world === current.world && seed.category === current.category);
      } else if (this.config.type === 'cart' && cart.length) {
        seeds = cart;
        sources = this.seedSources(seeds);
      } else if (historySeeds.length) {
        seeds = historySeeds;
        sources = this.seedSources(seeds);
        personalized = true;
      } else {
        const worlds = new Set();
        sources = this.sources.filter(source => { if (worlds.has(source.world)) return false; worlds.add(source.world); return true; }).slice(0, 6);
      }
      sources = [...new Map(sources.filter(source => source.url).map(source => [source.url, source])).values()].slice(0, 6);
      const requests = sources.map(source => this.request(source.url).then(cards => cards.filter(card => {
        if (collectionSource) return source.currentCollection || card.context.world === this.config.world;
        if (this.config.type === 'world') return this.config.world === 'branchen' || card.context.world === this.config.world;
        if (seeds.length) return seeds.some(seed => seed.world && seed.category && card.context.world === seed.world && card.context.category === seed.category);
        return card.context.world === source.world;
      })));
      if (current || (this.config.type === 'cart' && cart.length)) {
        for (const seed of seeds.filter(seed => seed.world && seed.category).slice(0, 2)) {
          const intent = this.config.type === 'cart' ? 'complementary' : 'related';
          requests.push(this.request(this.config.recommendations, { product_id: seed.id, limit: '10', intent }).then(cards => cards.filter(card => card.context.world === seed.world && (intent === 'complementary' || card.context.category === seed.category))));
        }
      }
      const groups = await Promise.all(requests);
      const excluded = new Set([...this.config.excluded, current?.id, ...history.map(card => card.context.id)]);
      const cards = [];
      // Prefer the current collection before supplementing with its reviewed world.
      if (collectionSource) {
        for (const group of groups) for (const card of group) {
          if (card?.available && !excluded.has(card.context.id)) { excluded.add(card.context.id); cards.push(card); }
        }
        return { cards: cards.slice(0, 32), personalized: false };
      }
      for (let index = 0; index < 24; index++) {
        for (const group of groups) {
          const card = group[index];
          if (card?.available && !excluded.has(card.context.id)) { excluded.add(card.context.id); cards.push(card); }
        }
      }
      if (cards.length <= 8 && seeds.some(seed => seed.world)) {
        const worlds = new Set(seeds.map(seed => seed.world).filter(Boolean));
        const requested = new Set(sources.map(source => source.url));
        const furtherSources = [...new Map(this.sources.filter(source => worlds.has(source.world) && !requested.has(source.url))
          .map(source => [source.url, source])).values()].slice(0, 4);
        const furtherGroups = await Promise.all(furtherSources.map(source => this.request(source.url)));
        const further = [];
        for (let index = 0; index < 24; index++) for (const group of furtherGroups) {
          const card = group[index];
          if (card?.available && worlds.has(card.context.world) && !excluded.has(card.context.id)) {
            excluded.add(card.context.id); further.push(card);
          }
        }
        return { cards, personalized, relatedCards: cards, inspiredCards: further.slice(0, 16) };
      }
      return { cards: cards.slice(0, 32), personalized };
    }
    async refresh() {
      this.updateManager();
      if (!this.ready || this.signal.aborted) return;
      const generation = ++this.generation;
      this.historyLimit = Math.min(8, this.state.items.length);
      const history = await this.hydrateHistory(8);
      if (generation !== this.generation || this.signal.aborted) return;
      this.historyCards = history;
      this.renderHistory();
      const result = await this.candidates(history);
      if (generation !== this.generation || this.signal.aborted) return;
      const related = this.rows.related;
      const inspired = this.rows.inspired;
      let title = this.word('neutral', 'Passende Produkte entdecken');
      if (this.config.type === 'product') title = this.word('product', 'Passend zu diesem Artikel');
      if (this.config.type === 'cart' && this.config.cart.length) title = this.word('cart', 'Passend zu deinem Warenkorb');
      if (this.config.type === 'world' || this.config.type === 'collection') title = this.word('world', 'Produkte aus dieser Kategorie');
      this.setHeading(related, result.personalized ? this.config.relatedTitle : title);
      this.setHeading(inspired, result.personalized ? this.config.inspiredTitle : this.word('more', 'Weitere Entdeckungen'));
      const split = Math.min(16, Math.max(8, result.cards.length - 8));
      const relatedCards = result.relatedCards || result.cards.slice(0, split);
      const inspiredCards = result.inspiredCards || result.cards.slice(split);
      this.renderCards(related, relatedCards);
      this.renderCards(inspired, inspiredCards);
      inspired.hidden = inspiredCards.length === 0;
      if (this.dialog.open) this.renderManager();
    }
    setHeading(row, title) {
      row.querySelector('h2').textContent = title;
      row.querySelector('[data-row-rail]').setAttribute('aria-label', title);
    }
    renderCards(row, cards, message = '') {
      const rail = row.querySelector('[data-row-rail]');
      rail.replaceChildren(...cards.map(card => card.node.cloneNode(true)));
      rail.scrollLeft = 0;
      row.querySelector('[data-row-carousel]').hidden = !cards.length;
      const notice = row.querySelector('[data-row-message]');
      notice.textContent = message || this.word('empty_recommendations', 'Zurzeit sind hier keine passenden Produkte verfügbar. Entdecke die Kategorien oben.');
      notice.hidden = cards.length > 0;
      requestAnimationFrame(() => this.updateCarousel(row));
    }
    historyMessage() {
      if (!this.allowed) return this.word('privacy', 'Dein Browserverlauf ist erst verfügbar, wenn deine Datenschutzeinstellungen die Speicherung erlauben.');
      if (this.storageFailed) return this.word('blocked', 'Dein Browser erlaubt auf diesem Gerät keine Speicherung des Verlaufs.');
      if (!this.state.enabled) return this.word('off', 'Dein Browserverlauf ist ausgeschaltet.');
      return this.word('empty_history', 'Du hast noch keine Artikel angesehen. Entdecke ein Produkt – es erscheint dann hier.');
    }
    renderHistory() { this.renderCards(this.rows.history, this.historyCards, this.historyMessage()); this.updateManager(); }
    columns() { return Number(getComputedStyle(this).getPropertyValue('--discovery-cols')) || 2; }
    updateCarousel(row) {
      const rail = row.querySelector('[data-row-rail]');
      const count = rail.children.length;
      const columns = this.columns();
      const cardWidth = rail.firstElementChild?.getBoundingClientRect().width || 1;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      const first = Math.round(rail.scrollLeft / (cardWidth + gap));
      const history = row === this.rows.history;
      const total = history && this.allowed && this.state.enabled ? count + Math.max(0, this.state.items.length - (this.historyLimit || 0)) : count;
      const pages = Math.max(1, Math.ceil(total / columns));
      const counter = row.querySelector('[data-row-counter]');
      const counterText = count ? this.word('page', 'Seite {page} von {pages}').replace('{page}', Math.min(pages, Math.ceil(first / columns) + 1)).replace('{pages}', pages) : '';
      if (counter.textContent !== counterText) counter.textContent = counterText;
      row.querySelector('[data-row-previous]').disabled = rail.scrollLeft < 5;
      row.querySelector('[data-row-next]').disabled = !count || (rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 3 && total <= count);
      if (history && total > count && rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 80) this.moreHistory();
    }
    moreHistory() {
      if (this.historyLoading?.generation === this.generation) return this.historyLoading.promise;
      if (!this.allowed || this.historyLimit >= this.state.items.length) return Promise.resolve();
      const generation = this.generation;
      this.historyLimit = Math.min(MAX_HISTORY, this.historyLimit + 8);
      const promise = this.hydrateHistory(this.historyLimit).then(cards => {
        if (generation !== this.generation || this.signal.aborted) return;
        const rail = this.rows.history.querySelector('[data-row-rail]');
        const existing = new Set([...rail.children].map(node => JSON.parse(node.dataset.context).id));
        cards.filter(card => !existing.has(card.context.id)).forEach(card => rail.append(card.node.cloneNode(true)));
        this.historyCards = cards;
        this.historyLoading = null;
        this.updateCarousel(this.rows.history);
      });
      this.historyLoading = { generation, promise };
      return promise;
    }
    async move(row, direction) {
      if (row === this.rows.history && direction > 0) await this.moreHistory();
      const rail = row.querySelector('[data-row-rail]');
      const width = rail.firstElementChild?.getBoundingClientRect().width || 0;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      rail.scrollBy({ left: direction * (width + gap) * this.columns(), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      this.updateCarousel(row);
    }
    onKey(event, row) {
      if (event.target !== event.currentTarget || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key.startsWith('Arrow')) this.move(row, event.key === 'ArrowLeft' ? -1 : 1);
      else row.querySelector('[data-row-rail]').scrollTo({ left: event.key === 'Home' ? 0 : row.querySelector('[data-row-rail]').scrollWidth });
    }
    onClick(event) {
      const target = event.target.closest('button, a');
      if (!target || !this.contains(target)) return;
      if (target.matches('[data-row-previous], [data-row-next]')) this.move(target.closest('[data-discovery-row]'), target.hasAttribute('data-row-next') ? 1 : -1);
      else if (target.matches('[data-history-open]')) {
        event.preventDefault(); this.opener = target; this.dialog.showModal(); this.renderManager();
      } else if (target.matches('[data-history-close]')) this.dialog.close();
      else if (target.matches('[data-history-clear]')) {
        this.recorded = true; this.state.items = []; this.persist(); this.refresh();
      } else if (target.matches('[data-history-remove]')) {
        this.recorded = true; this.state.items = this.state.items.filter(item => item.id !== target.dataset.historyRemove); this.persist(); this.refresh();
      } else if (target.matches('[data-history-privacy]')) {
        const opener = document.querySelector('footer a[href="#shopifyReshowConsentBanner"]');
        if (opener) { event.preventDefault(); this.dialog.close(); opener.click(); }
      }
    }
    updateManager() {
      const checkbox = this.querySelector('[data-history-enabled]');
      checkbox.checked = this.allowed && this.state.enabled && !this.storageFailed;
      checkbox.disabled = !this.allowed || this.storageFailed;
      this.querySelector('[data-history-clear]').disabled = !this.allowed || !this.state.items.length;
      this.querySelector('[data-history-status]').textContent = this.historyMessage();
      if (!this.allowed || !this.state.items.length) {
        const list = this.querySelector('[data-history-list]');
        const hadFocus = list.contains(document.activeElement);
        list.replaceChildren();
        if (hadFocus) (checkbox.disabled ? this.querySelector('[data-history-close]') : checkbox).focus({ preventScroll: true });
      }
    }
    async renderManager() {
      this.updateManager();
      const generation = this.generation;
      const cards = await this.hydrateHistory(MAX_HISTORY);
      if (this.signal.aborted || generation !== this.generation || !this.dialog.open || !this.allowed) return;
      const items = cards.map(card => {
        const item = document.createElement('div'); item.className = 'shopde-history-dialog__item';
        const link = document.createElement('a'); link.href = card.node.querySelector('a').href;
        const img = card.node.querySelector('img').cloneNode(true); img.removeAttribute('sizes'); img.width = 65; img.height = 65;
        const name = document.createElement('span'); name.textContent = card.node.querySelector('.shopde-discovery-card__name').textContent;
        link.append(img, name);
        const button = document.createElement('button'); button.type = 'button'; button.className = 'shopde-history-dialog__remove'; button.dataset.historyRemove = card.context.id; button.textContent = this.word('remove', 'Entfernen'); button.setAttribute('aria-label', `${button.textContent}: ${name.textContent}`);
        item.append(link, button); return item;
      });
      const list = this.querySelector('[data-history-list]');
      const focusIndex = [...list.querySelectorAll('[data-history-remove]')].indexOf(document.activeElement);
      list.replaceChildren(...items);
      if (focusIndex >= 0) (items[Math.min(focusIndex, items.length - 1)]?.querySelector('button') || this.querySelector('[data-history-clear]')).focus({ preventScroll: true });
      if (cards.length) this.querySelector('[data-history-status]').textContent = this.word('saved', '{count} angesehene Artikel auf diesem Gerät.').replace('{count}', cards.length);
    }
  }
  customElements.define('shopde-discovery', ShopdeDiscovery);
})();
